/**
 * `/p/$projectId…`: a project tab (docs/ux/project-tab-spec.md §1, navigation-ia.md §3). The
 * shell's tab strip is on top; this file renders the project chrome and its views: the Lanes body
 * over existing data (inspector `?i=<kind>:<id>` in the shell's slot), and the run's full
 * conversation. `/p` opens the last-viewed tab (Home when there are no projects).
 */
import {
  Link,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useRouter,
  useRouterState,
  useSearch,
} from '@tanstack/react-router'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAppUi } from '../components/app-ui.js'
import { BARK_SLOTS } from '../components/bark/index.js'
import { SinceLine } from '../components/Changelog.js' // g13-digests
import { OutsideViko } from '../components/Ingest.js' // ingest-sync
import { INSPECT_MARK, Inspector } from '../components/inspector/Inspector.js'
import { Keys } from '../components/Keys.js'
import { GoalTree } from '../components/lanes/GoalTree.js'
import { GoalHead } from '../components/lanes/goal-strip.js'
import { LanesView } from '../components/lanes/LanesView.js'
import { useLivePhases } from '../components/lanes/LiveRuns.js'
import { provideLanesSlots, useLanesSlots } from '../components/lanes/slots.js'
import { PlanCards } from '../components/PlanCard.js' // g18-batch
import { RowActionsProvider } from '../components/RowActions.js'
import { SessionView } from '../components/SessionView.js'
import { ScopingBanner } from '../components/setup/ScopingBanner.js' // g5-setup
import { InspectorSlot } from '../components/shell/slots.js'
import { itemFreshness } from '../lib/inbox-freshness.js'
// ---- now-inbox: decision slots for the tab ----
import '../components/decision/lanes-slots.js'
import { useDecisions } from '../components/decision/useDecisions.js' // ia-shell
import { useNeedsYou } from '../components/inbox/useNeedsYou.js'
import { LockedPanel, useLocked } from '../components/lock/ContentLock.js'
// ---- end now-inbox ----
import { useProjectTabs } from '../components/tabs/useProjectTabs.js'
import { Empty, errorText, SkeletonRows } from '../components/ui.js'
import { useGoal } from '../lib/goals.js'
import { PROJECT_VIEWS, type ProjectView, projectHref, projectViewOfPath } from '../lib/ia.js'
import { buildLanesModel, isLiveRun, type LaneCard, type LanesInput } from '../lib/lanes-model.js'
import { defaultProject, rememberProject } from '../lib/last-project.js'
import {
  useBuilds,
  useConnection,
  useInbox,
  useLandings,
  useMachines,
  useRuns,
  useTasks,
  useWorkstreams,
} from '../lib/queries.js'
import { isTyping } from '../lib/shortcut.js'
import { TabNumberContext, tabNumberOf } from '../lib/tab-number.js'
import type { Project } from '../lib/types.js'
import './project-tab.css'

// The real bark box, outcome feed and bark inspector (with discuss) replace the stand-ins.
provideLanesSlots(BARK_SLOTS)

/** `/p`: the remembered (else first pinned) project tab. */
export function ProjectTabIndex() {
  const tabs = useProjectTabs()
  if (tabs.projects.isPending) return <SkeletonRows n={4} />
  const id = defaultProject(tabs.pinned.map((p) => p.id)) ?? tabs.projects.data?.[0]?.id
  if (!id) return <Navigate to={'/home' as never} replace />
  return <Navigate to="/p/$projectId" params={{ projectId: id }} replace />
}

/**
 * `/p/$projectId` layout: the project's chrome (name, goal, the view switcher) around its views —
 * Lanes (index), History, Builds (`releases`), Artifacts, Settings and a run's full conversation
 * (`s/$runId`). Everything scoped to this project renders here, so the tab stays highlighted and
 * the URL stays under `/p/$id` (navigation-ia.md §3.4). View keys: `g l` `g h` `g b` `g a` `,`.
 */
export function ProjectTabPage() {
  const { projectId } = useParams({ strict: false }) as { projectId: string }
  const nav = useNavigate()
  const tabs = useProjectTabs()
  const project = tabs.projects.data?.find((p) => p.id === projectId)
  const contentLocked = useLocked()
  const view = useRouterState({ select: (s) => projectViewOfPath(s.location.pathname) })

  useEffect(() => {
    if (project) rememberProject(project.id)
  }, [project])

  // View keys (typing-guarded): g l / g h / g b / g a, and `,` for this project's settings.
  useEffect(() => {
    let gAt = 0
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      if (e.key === ',') {
        e.preventDefault()
        void nav({ href: projectHref(projectId, 'settings') })
        return
      }
      if (e.key === 'g') {
        gAt = Date.now()
        return
      }
      if (!gAt || Date.now() - gAt > 1200) return
      gAt = 0
      const target = VIEW_KEYS[e.key]
      if (target === undefined) return
      e.preventDefault()
      void nav({ href: projectHref(projectId, target) })
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [nav, projectId])

  // Mobile: swipe left/right between tabs.
  const touch = useRef<{ x: number; y: number } | null>(null)
  const swipe = (dir: 1 | -1) => {
    const i = tabs.pinned.findIndex((p) => p.id === projectId)
    const next = tabs.pinned[i + dir]
    if (next)
      void nav({
        href: projectHref(next.id, view === 'run' || view === 'work' ? '' : (view ?? '')),
      })
  }

  if (!project) {
    const locked = tabs.projects.isError && /locked/i.test(errorText(tabs.projects.error))
    // Locked in this browser: the lock panel (one-tap unlock), not a "missing project".
    if (contentLocked)
      return (
        <div className="lk-page">
          <LockedPanel where="project" />
        </div>
      )
    return (
      <div className="lk-page">
        {tabs.projects.isPending ? (
          <SkeletonRows n={6} />
        ) : (
          <Empty
            center
            title={locked ? 'Unlock to see this project' : 'This project is not in your tabs'}
          >
            {locked
              ? 'Project content is end-to-end encrypted. Unlock this browser to read it.'
              : 'It may have been archived, or it belongs to another account.'}
          </Empty>
        )}
      </div>
    )
  }
  return (
    <ProjectContext.Provider value={project}>
      <TabNumberContext.Provider value={tabNumberOf(tabs.pinned, project.id)}>
        <div
          className={`lk-page${view === 'run' ? ' is-run' : ''}`}
          onTouchStart={(e) => {
            if (view === 'run') return
            const t = e.touches[0]
            touch.current = t ? { x: t.clientX, y: t.clientY } : null
          }}
          onTouchEnd={(e) => {
            const s = touch.current
            const t = e.changedTouches[0]
            touch.current = null
            if (!s || !t) return
            const dx = t.clientX - s.x,
              dy = t.clientY - s.y
            // Horizontal scrollers (the lanes board, the view switcher) keep their own swipes.
            if ((e.target as HTMLElement).closest?.('.lk-views, .lk-board')) return
            if (Math.abs(dx) > 80 && Math.abs(dx) > 2 * Math.abs(dy)) swipe(dx < 0 ? 1 : -1)
          }}
        >
          {view === 'run' ? null : <ProjectHeader project={project} view={view ?? ''} />}
          <Outlet />
        </div>
      </TabNumberContext.Provider>
    </ProjectContext.Provider>
  )
}

const VIEW_KEYS: Record<string, ProjectView> = {
  l: '',
  t: 'goal',
  h: 'history',
  b: 'releases',
  a: 'artifacts',
}

/**
 * The project's page head (refinement spec §2, WP-3): name, goal and current milestone on one
 * line with the view switcher, and the since-you-looked digest under it (hidden when there is
 * nothing to say). Every number opens what it counts.
 */
export function ProjectHeader({ project, view }: { project: Project; view: ProjectView | 'work' }) {
  const nav = useNavigate()
  const tasks = useTasks({ projectId: project.id })
  const landings = useLandings(project.id)
  const builds = useBuilds(project.id)
  const goal = useGoal(project.id, project.goal ?? '', {
    landings: landings.data ?? [],
    builds: builds.data ?? [],
    tasks: tasks.data ?? [],
  })
  // The goal inspector lives on the Lanes view: open it there from any view.
  const inspect = (key: string) =>
    view === ''
      ? void nav({ to: '.', search: (s: Record<string, unknown>) => ({ ...s, i: key }) })
      : void nav({ href: projectHref(project.id, '', key) })
  return (
    <ProjectHead
      project={project}
      view={view}
      goal={<GoalHead goal={goal} onInspect={inspect} />}
      since={<SinceLine projectId={project.id} />}
    />
  )
}

/** The head's layout (also used by the mock-only `/dev/lanes` gallery with fixture data). */
export function ProjectHead({
  project,
  view,
  goal,
  since,
}: {
  project: Pick<Project, 'id' | 'name'>
  view: ProjectView | 'work'
  goal?: React.ReactNode
  since?: React.ReactNode
}) {
  const chord = useGChord()
  return (
    <header className="lk-phead">
      <div className="lk-phead__row">
        <div className="lk-phead__title">
          <h1>{project.name}</h1>
          {goal}
        </div>
        <nav
          className="lk-views"
          data-scroll-x
          aria-label={`${project.name} views`}
          data-chord={chord ? '' : undefined}
        >
          {PROJECT_VIEWS.map((v) => (
            <Link
              key={v.id || 'lanes'}
              to={projectHref(project.id, v.id)}
              className="lk-view"
              activeOptions={{ exact: true, includeSearch: false }}
              aria-current={view === v.id ? 'page' : undefined}
              aria-keyshortcuts={v.keys === ',' ? ',' : v.keys}
            >
              <span>{v.label}</span>
              <Keys k={v.keys} />
            </Link>
          ))}
        </nav>
      </div>
      {since}
    </header>
  )
}

/**
 * app-clarity: view keycaps show only while a `g` chord is open (the status line and `?` list
 * them otherwise), so the switch reads as words.
 */
function useGChord(): boolean {
  const [on, setOn] = useState(false)
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'g' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) {
        if (t) {
          clearTimeout(t)
          t = undefined
          setOn(false)
        }
        return
      }
      setOn(true)
      clearTimeout(t)
      t = setTimeout(() => setOn(false), 1200)
    }
    addEventListener('keydown', onKey)
    return () => {
      removeEventListener('keydown', onKey)
      clearTimeout(t)
    }
  }, [])
  return on
}

/** The project of the current `/p/$id` layout (views read it instead of refetching). */
export const ProjectContext = createContext<Project | null>(null)
export function useTabProject(): Project {
  const p = useContext(ProjectContext)
  if (!p) throw new Error('useTabProject outside a project tab')
  return p
}

/** `/p/$projectId` index: the Lanes body with the inspector (`?i=`) in the shell's slot. */
export function LanesTab() {
  const project = useTabProject()
  const search = useSearch({ strict: false }) as { i?: string }
  const nav = useNavigate()
  const ui = useAppUi()

  const inspect = useCallback(
    (key: string) => {
      try {
        performance.clearMarks(INSPECT_MARK)
        performance.mark(INSPECT_MARK)
      } catch {
        // ignore
      }
      void nav({ to: '.', search: (s: Record<string, unknown>) => ({ ...s, i: key }) })
    },
    [nav],
  )
  const closeInspector = useCallback(
    () =>
      void nav({
        to: '.',
        search: (s: Record<string, unknown>) => {
          const { i: _, ...rest } = s
          return rest
        },
      }),
    [nav],
  )

  // Tab switch: land in this tab's bark box (or its first hot decision) unless an inspector is open.
  const body = useRef<HTMLDivElement>(null)
  const first = useRef(true)
  // biome-ignore lint/correctness/useExhaustiveDependencies: only on tab switch
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    if (search.i || matchMedia?.('(pointer: coarse)').matches) return
    const t = setTimeout(() => {
      const root = body.current
      const input = root?.querySelector<HTMLTextAreaElement>('[data-primary-input]:not([disabled])')
      // A blocking ask (amber count in the Needs you head) takes focus before the composer.
      const hotAsk = root
        ?.querySelector('.lk-asks:has(.lk-count.is-wait)')
        ?.querySelector<HTMLElement>('[data-row-main], .lk-ask__q')
      if (hotAsk && input && !input.value) hotAsk.focus()
      else input?.focus()
    }, 0)
    return () => clearTimeout(t)
  }, [project.id])

  return (
    <div className="lk-tabbody" ref={body}>
      <LanesTabBody
        project={project}
        inspectKey={search.i}
        onInspect={inspect}
        onClose={closeInspector}
        onStart={(c) =>
          ui.openStart({
            projectId: project.id,
            ...(c.workstreamId ? { workstreamId: c.workstreamId, workstreamName: c.laneName } : {}),
            taskId: c.id,
            prompt: c.title,
          })
        }
      />
    </div>
  )
}

/**
 * `/p/$projectId/goal`: the goal's orchestration tree. Its questions, fixes and tasks open in the
 * Lanes view's inspector (`?i=`), where the decision view lives.
 */
export function GoalTab() {
  const project = useTabProject()
  const nav = useNavigate()
  const { model, goal, loading, probes } = useLanesModel(project)
  const text = goal.view?.goal.text ?? project.goal
  return (
    <div className="lk-tabbody">
      <div className="lk-scroll">
        {loading ? (
          <SkeletonRows n={6} />
        ) : (
          <GoalTree
            model={model}
            goal={text}
            onInspect={(key) => void nav({ href: projectHref(project.id, '', key) })}
          />
        )}
      </div>
      {probes}
    </div>
  )
}

/** `/p/$projectId/s/$runId`: the full conversation in the project's chrome. */
export function ProjectRunPage() {
  const project = useTabProject()
  const { runId } = useParams({ strict: false }) as { runId: string }
  const search = useSearch({ strict: false }) as { seq?: number; at?: string }
  const nav = useNavigate()
  const router = useRouter()
  // `?at=decision:<id>` ("Open in session", ⇧V): resolve the ask's seq so the viewer loads there.
  const decisions = useDecisions()
  const atSeq =
    search.at?.startsWith('decision:') && !search.seq
      ? decisions.data?.find((d) => d.id === search.at?.slice('decision:'.length))?.seq
      : undefined
  useEffect(() => {
    if (atSeq)
      void nav({
        to: '.',
        search: (s: Record<string, unknown>) => ({ ...s, seq: atSeq }),
        replace: true,
      })
  }, [atSeq, nav])
  if (atSeq) return <SkeletonRows n={4} />
  return (
    <SessionView
      key={runId}
      runId={runId}
      onBack={() => {
        // Back returns to where the run was opened (the tab with its inspector still open).
        const idx = (window.history.state as { __TSR_index?: number } | null)?.__TSR_index ?? 0
        if (idx > 0) router.history.back()
        else void nav({ href: projectHref(project.id) })
      }}
    />
  )
}

function LanesTabBody({
  project,
  inspectKey,
  onInspect,
  onClose,
  onStart,
}: {
  project: Project
  inspectKey: string | undefined
  onInspect: (k: string) => void
  onClose: () => void
  onStart: (c: LaneCard) => void
}) {
  // Starter text for the composer: the goal inspector's "Ask Viko for milestones" fills it too.
  const [prefill, setPrefill] = useState<{ text: string; nonce: number }>()
  const starter = useCallback((text: string) => setPrefill({ text, nonce: Date.now() }), [])
  const { model, input, goal, loading, probes, frozenAt } = useLanesModel(project)
  const slots = useLanesSlots()
  return (
    <div className="lk-with-inspector">
      <div className="lk-scroll">
        {/* ---- g5-setup: resume a project that is still being scoped ---- */}
        <ScopingBanner project={project} />
        {/* ---- end g5-setup ---- */}
        {loading ? (
          <SkeletonRows n={6} />
        ) : (
          <RowActionsProvider
            projectId={project.id}
            handlers={{ details: (e) => onInspect(`${e.kind}:${e.id}`) }}
          >
            <LanesView
              model={model}
              slots={slots}
              onInspect={onInspect}
              onStart={onStart}
              onStarter={starter}
              // ingest-sync: work that happened outside Viko, one row under Needs you
              outside={
                <>
                  {/* g18-batch: proposed plans, one card each */}
                  <PlanCards projectId={project.id} />
                  <OutsideViko projectId={project.id} />
                </>
              }
              {...(prefill ? { prefill } : {})}
              {...(inspectKey ? { inspectKey } : {})}
              {...(frozenAt ? { frozenAt } : {})}
            />
          </RowActionsProvider>
        )}
      </div>
      {inspectKey && !loading ? (
        <InspectorSlot>
          <Inspector
            inspectKey={inspectKey}
            model={model}
            input={input}
            slots={slots}
            onInspect={onInspect}
            onClose={onClose}
            goal={goal}
            onStarter={starter}
          />
        </InspectorSlot>
      ) : null}
      {probes}
    </div>
  )
}

/**
 * The project's lanes model over live data (Lanes and Orchestration share it): workstreams, tasks,
 * runs, the live asks, landings, builds and machines, plus the live-phase probes to render.
 */
export function useLanesModel(project: Project) {
  const workstreams = useWorkstreams(project.id)
  const tasks = useTasks({ projectId: project.id })
  const runs = useRuns()
  const inbox = useInbox()
  const landings = useLandings(project.id)
  const builds = useBuilds(project.id)
  const machines = useMachines()
  const conn = useConnection()
  const needs = useNeedsYou()
  const goal = useGoal(project.id, project.goal ?? '', {
    landings: landings.data ?? [],
    builds: builds.data ?? [],
    tasks: tasks.data ?? [],
  })
  const liveRuns = useMemo(
    () => (runs.data ?? []).filter((r) => r.projectId === project.id && isLiveRun(r)),
    [runs.data, project.id],
  )
  const { phases, probes } = useLivePhases(liveRuns)
  // Hub disconnected: live lines freeze "as of" the moment the connection dropped.
  const [frozenAt, setFrozenAt] = useState<number>()
  useEffect(() => {
    setFrozenAt(conn === 'offline' || conn === 'reconnecting' ? Date.now() : undefined)
  }, [conn])
  const now = useMinute()
  // Blockers whose run has ended or moved on are not asks (lib/inbox-freshness.ts).
  const liveInbox = useMemo(
    () =>
      (inbox.data ?? []).filter(
        (i) =>
          itemFreshness(i, {
            machines: machines.data ?? [],
            landings: landings.data ?? [],
            runs: runs.data ?? [],
            now,
          })?.state !== 'resolved',
      ),
    [inbox.data, machines.data, landings.data, runs.data, now],
  )
  const input: LanesInput = useMemo(
    () => ({
      project,
      workstreams: workstreams.data ?? [],
      tasks: tasks.data ?? [],
      runs: runs.data ?? [],
      inbox: liveInbox,
      landings: landings.data ?? [],
      builds: builds.data ?? [],
      machines: machines.data ?? [],
      live: phases,
      connection: conn,
      needsYou: needs.of, // one count source: lib/needs-you.ts
      now,
    }),
    [
      needs,
      project,
      workstreams.data,
      tasks.data,
      runs.data,
      liveInbox,
      landings.data,
      builds.data,
      machines.data,
      phases,
      conn,
      now,
    ],
  )
  const model = useMemo(() => buildLanesModel(input), [input])
  const loading = workstreams.isPending || tasks.isPending
  return { model, input, goal, loading, probes, frozenAt }
}

/** A clock that ticks once a minute (landed-today, waiting times). */
function useMinute() {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [])
  return now
}
